/**
 * OutletManagementPage — `/settings/outlet-management`.
 *
 * Backed end-to-end by service-customer:
 *   - GET    /v1/outlets                  — public list (read)
 *   - POST   /v1/outlets/admin            — create
 *   - PATCH  /v1/outlets/admin/:id        — update
 *   - DELETE /v1/outlets/admin/:id        — delete
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createOutlet,
  deleteOutlet,
  listOutletsAdmin,
  type OutletDto,
  type OutletInput,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

const QUERY_KEY = ['outlets', 'admin'] as const;

export function OutletManagementPage(): JSX.Element {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<OutletInput>({
    outletCode: '',
    name: '',
    addressLine1: '',
    city: '',
    lga: '',
    state: '',
    services: ['DROPOFF'],
    openingHours: { mon: '08:00-17:00' },
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);

  const outlets = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listOutletsAdmin({ pageSize: 100 }),
    retry: false,
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: (input: OutletInput) => createOutlet(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
      setAdding(false);
      setDraft({
        outletCode: '',
        name: '',
        addressLine1: '',
        city: '',
        lga: '',
        state: '',
        services: ['DROPOFF'],
        openingHours: { mon: '08:00-17:00' },
        isActive: true,
      });
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteOutlet(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const denied =
    outlets.isError &&
    outlets.error instanceof ApiError &&
    (outlets.error.status === 401 || outlets.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Outlet management"
        description="NIPOST and franchise outlet configurations."
        actions={
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Add outlet
          </button>
        }
      />

      {outlets.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          Permission required: the operator token must carry{' '}
          <code className="font-mono">Outlet/manageAdmin</code>.
        </div>
      ) : outlets.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Service unreachable: {(outlets.error as Error).message}
        </div>
      ) : (outlets.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No outlets registered.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Code</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Name</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">City</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">LGA</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">State</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Services</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {outlets.data?.items.map((o: OutletDto) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono">{o.outletCode}</td>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">{o.name}</td>
                  <td className="px-4 py-2 text-xs">{o.city}</td>
                  <td className="px-4 py-2 text-xs text-muted">{o.lga}</td>
                  <td className="px-4 py-2 text-xs text-muted">{o.state}</td>
                  <td className="px-4 py-2 text-[11px] text-muted">{o.services.join(', ')}</td>
                  <td className="px-4 py-2 text-xs">{o.isActive ? 'ACTIVE' : 'INACTIVE'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => deleteMut.mutate(o.id)}
                      aria-label={`Delete ${o.name}`}
                      className="text-red-600 hover:bg-red-50 rounded p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="text-base font-bold text-gray-900">Add outlet</p>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="p-1.5 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted">Outlet code</label>
                <input
                  type="text"
                  value={draft.outletCode}
                  aria-label="Outlet code"
                  onChange={(e) => setDraft({ ...draft, outletCode: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted">Name</label>
                <input
                  type="text"
                  value={draft.name}
                  aria-label="Outlet name"
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] font-semibold uppercase text-muted">Address line 1</label>
                <input
                  type="text"
                  value={draft.addressLine1}
                  aria-label="Address line 1"
                  onChange={(e) => setDraft({ ...draft, addressLine1: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted">City</label>
                <input
                  type="text"
                  value={draft.city}
                  aria-label="City"
                  onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted">LGA</label>
                <input
                  type="text"
                  value={draft.lga}
                  aria-label="LGA"
                  onChange={(e) => setDraft({ ...draft, lga: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase text-muted">State</label>
                <input
                  type="text"
                  value={draft.state}
                  aria-label="State"
                  onChange={(e) => setDraft({ ...draft, state: e.target.value })}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
            </div>
            {error ? (
              <div className="px-4 pb-2 text-xs text-red-600">{error}</div>
            ) : null}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => createMut.mutate(draft)}
                disabled={createMut.isPending}
                className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-60"
              >
                {createMut.isPending ? 'Saving…' : 'Create outlet'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
